import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowLeft, KeyRound, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { setError('Reset link is missing or invalid.'); return; }
    if (pwd.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (pwd !== pwd2) { setError('The two passwords don\'t match.'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in-up">
        <Link to="/" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-8 transition-colors">
          <ArrowLeft size={18} /><span className="text-sm font-medium">Back to Home</span>
        </Link>

        <Card className="border-stone-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-3">
              <KeyRound size={26} className="text-orange-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Set a new password</CardTitle>
            <CardDescription className="text-stone-500">Pick something memorable. Minimum 6 characters.</CardDescription>
          </CardHeader>
          <CardContent>
            {!token && (
              <div data-testid="reset-token-missing" className="p-3 rounded-lg bg-amber-50 text-amber-800 text-sm border border-amber-200 flex items-start gap-2 mb-4">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>This page expects a <code>?token=</code> in the URL. Use the link from the password-reset email.</span>
              </div>
            )}

            {done ? (
              <div data-testid="reset-success" className="text-center py-4 space-y-3">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <CheckCircle className="text-green-600" size={28} />
                </div>
                <p className="text-stone-800 font-semibold">Password updated</p>
                <p className="text-sm text-stone-500">You can now sign in with your new password.</p>
                <Button data-testid="reset-go-login-btn" onClick={() => navigate('/login')} className="bg-orange-600 hover:bg-orange-700 text-white w-full">
                  Go to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {error && <div data-testid="reset-error" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}
                <div className="space-y-2">
                  <Label className="text-stone-700">New password</Label>
                  <Input data-testid="reset-password-input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Min 6 characters" minLength={6} required className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label className="text-stone-700">Confirm new password</Label>
                  <Input data-testid="reset-password-confirm-input" type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} placeholder="Repeat the same password" minLength={6} required className="h-11" />
                </div>
                <Button data-testid="reset-submit-btn" type="submit" disabled={loading || !token} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-semibold">
                  {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Updating…</> : 'Update password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
